<template>
  <div
    v-if="
      hasSale &&
      (discountPercentage !== undefined || discountAmount !== undefined)
    "
    class="sale-banner"
  >
    <LightningChargeFill class="w-5 h-5 text-orange-600 flex-shrink-0" />
    <span class="text-sm font-medium text-orange-900">
      {{ saleMessage }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import type { EventDescription } from '~/composables/types/event'
import LightningChargeFill from '~/assets/icons/lightning-charge-fill.svg?component'
import { formatPriceAmount } from '~/utils/currencyHelpers'

interface Props {
  hasSale: boolean
  eventData: EventDescription
  isPrivate?: boolean
}

const props = defineProps<Props>()

const { t, locale } = useI18n()

// Get discount percentage from promotion data (only for PERCENTAGE_VALUE type)
const discountPercentage = computed(() => {
  const promotion = props.eventData?.activePromotion?.data

  // Return undefined if promotion or discount is not available
  if (!promotion || promotion.discount === undefined) {
    return undefined
  }

  // Only return percentage if discount_type is PERCENTAGE
  if (promotion.discount_type === 'PERCENTAGE') {
    return Math.round(promotion.discount)
  }
  return undefined
})

// Get discount amount formatted as currency (only for FIXED_VALUE type)
const discountAmount = computed(() => {
  const promotion = props.eventData?.activePromotion?.data

  // Return undefined if promotion or discount is not available
  if (!promotion || promotion.discount === undefined) {
    return undefined
  }

  if (promotion.discount_type === 'FIXED_VALUE') {
    // discount is in cents, format it as currency
    return formatPriceAmount(promotion.discount, locale.value, 'EUR')
  }

  return undefined
})

const saleMessage = computed(() => {
  // Handle percentage discount
  if (discountPercentage.value !== undefined) {
    const translationKey = props.isPrivate
      ? 'product.sale.bannerGroupTickets'
      : 'product.sale.bannerAllTickets'

    return t(translationKey, {
      percent: discountPercentage.value,
    })
  }

  // Handle fixed value discount
  if (discountAmount.value !== undefined) {
    const translationKey = props.isPrivate
      ? 'product.sale.bannerGroupTicketsFixed'
      : 'product.sale.bannerAllTicketsFixed'

    return t(translationKey, {
      amount: discountAmount.value,
    })
  }

  return ''
})
</script>

<style lang="postcss" scoped>
.sale-banner {
  @apply p-3 flex items-center justify-center gap-2 mb-1;
  background-color: #fff7ed;
  color: #e07050;
}
</style>
